import styled from 'styled-components';
import { Container as ContainerBase } from '../Layout/Container.styles';

export const HeaderWrap = styled.header`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  z-index: 9999;
  background: ${props => props.theme.palette.background};
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  box-shadow: 0 1px 0 rgba(0,0,0,0.04);
  border-bottom: 1px solid ${props => props.theme.palette.border};
  transition: top 180ms ease;

  /* When the announcement is present, nudge header below it */
  body.has-announcement & {
    top: 40px;
  }
  @media (max-width: 768px) {
    /* on mobile the announcement is slightly taller */
    body.has-announcement & {
      top: 79px;
    }
  }
`;

export const HeaderInner = styled(ContainerBase)`
  /* 1440px reference capture shows 80px nav height */
  height: 80px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  column-gap: 32px;
  @media (max-width: 768px) {
    display: none;
  }
`;

export const Brand = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1.5)};
  font-weight: ${props => props.theme.typography.weights.bold};
  font-size: ${props => props.theme.typography.sizes.lg};
  letter-spacing: -0.01em;
  color: ${props => props.theme.palette.textPrimary};
`;

export const NavLinks = styled.nav`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  gap: 5px;
  font-size: 15px;
  font-weight: ${props => props.theme.typography.weights.semibold};
  color: ${props => props.theme.palette.textSecondary};
  @media (max-width: 768px) {
    display: none;
  }
`;

export const NavLink = styled.a`
  color: inherit;
  font-size: ${props => props.theme.typography.sizes.sm};
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing(0.5)};
  padding: 8px 14px;
  letter-spacing: -0.01em;
  transition: color 120ms ease;
  position: relative;

  &:hover {
    color: ${props => props.theme.palette.textPrimary};
  }
  
  &.open {
    background: ${props => props.theme.palette.accentSubtle};
    color: ${props => props.theme.palette.accent};
    border-radius: 8px;
  }
`;

export const Caret = styled.span`
  width: 6px;
  height: 6px;
  display: inline-flex;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: rotate(45deg) translateY(-1px);
  margin-left: 6px;
  margin-top: -2px;
  transition: transform 160ms ease;

  /* rotate when parent NavLink has open class */
  ${NavLink}.open & {
    transform: rotate(-135deg) translateY(-1px);
  }
`;

export const Actions = styled.div`
  justify-self: end;
  display: inline-flex;
  gap: ${props => props.theme.spacing(1)};
  align-items: center;
  @media (max-width: 768px) {
    display: none;
  }
`;

export const TextLink = styled.a`
  font-size: ${props => props.theme.typography.sizes.sm};
  font-weight: ${props => props.theme.typography.weights.semibold};
  color: ${props => props.theme.palette.textSecondary};
  padding: 10px 12px;
  border-radius: 10px;
  transition: background 120ms ease, color 120ms ease;

  &:hover {
    background: ${props => props.theme.palette.accentSubtle};
    color: ${props => props.theme.palette.accent};
  }
`;

export const LogoMark = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(4, 1fr);
  gap: 3px;
  padding: 4px;
  background: ${props => props.theme.palette.backgroundSoft};

  span {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: ${props => props.theme.palette.accent};
  }

  span:nth-child(1),
  span:nth-child(2),
  span:nth-child(5),
  span:nth-child(6) {
    background: #1C64F2;
  }

  span:nth-child(3),
  span:nth-child(4),
  span:nth-child(7),
  span:nth-child(8) {
    background: #2F80ED;
  }

  span:nth-child(9),
  span:nth-child(10),
  span:nth-child(13),
  span:nth-child(14) {
    background: #5AA0F5;
  }

  span:nth-child(11),
  span:nth-child(12),
  span:nth-child(15),
  span:nth-child(16) {
    background: #8AC3FF;
  }
`;

/* Mobile header bar */
export const MobileBar = styled.div`
  display: none;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  height: 56px;
  @media (max-width: 768px) {
    display: flex;
  }
`;

export const MobileMenuButton = styled.button`
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: none;
  background: none;
  color: ${props => props.theme.palette.textSecondary};
  cursor: pointer;
`;

export const MobileBrand = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  font-weight: ${props => props.theme.typography.weights.bold};
  font-size: ${props => props.theme.typography.sizes.md};
  color: ${props => props.theme.palette.textPrimary};
  flex: 1;
  justify-content: center;
  text-align: center;
`;

export const MobileActions = styled.div`
  display: inline-flex;
  gap: ${props => props.theme.spacing(0.5)};
  align-items: center;
`;

/* Fullscreen mobile menu (simple slide-over) */
export const MobileMenu = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10001;
  background: rgb(18 18 18 / 91%);
  display: flex;
  flex-direction: column;
  padding: 80px 20px 20px;
  -webkit-overflow-scrolling: touch;
  @media (min-width: 769px) {
    display: none;
  }

  nav {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    background: rgb(18 18 18 / 91%);
    a {
      color: #fff;
      font-size: 18px;
      font-weight: ${props => props.theme.typography.weights.semibold};
      padding: 14px 20px;
      display: block;
      text-align: left;
    }
  }

  .mobile-close {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 44px;
    height: 44px;
    border-radius: 10px;
    border: none;
    background: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #fff;
  }
`;
